import * as React from 'react';

import type { Content, Editor } from '@tiptap/react';
import type { UseMinimalTiptapEditorProps } from './hooks/use-minimal-tiptap';
import { EditorContent } from '@tiptap/react';
import { cn } from '@/lib/utils';
import { SectionTwo } from './components/section/two';
import { LinkBubbleMenu } from './components/bubble-menu/link-bubble-menu';
import { useMinimalTiptapEditor } from './hooks/use-minimal-tiptap';
import { MeasuredContainer } from './components/measured-container';
import './styles/tiptap.css';
import { Separator } from '@/components/ui/separator';
import { LinkEditPopover } from '@/components/commons/minimal-tiptap/components/link/link-edit-popover';
import SectionThree from '@/components/commons/minimal-tiptap/components/section/three';

export interface MinimalTiptapProps extends Omit<UseMinimalTiptapEditorProps, 'onUpdate'> {
  value?: Content;
  onChange?: (value: Content) => void;
  className?: string;
  editorContentClassName?: string;
  maxLength?: number;
  enableTextColor?: boolean;
}

const Toolbar = ({ editor, enableTextColor }: { editor: Editor; enableTextColor?: boolean }) => (
  <div className='shrink-0 overflow-x-auto border-b border-border p-2'>
    <div className='flex w-max items-center gap-px'>
      <SectionTwo
        editor={editor}
        activeActions={['bold', 'italic', 'underline', 'strikethrough', 'clearFormatting']}
        mainActionCount={2}
      />
      {enableTextColor && <SectionThree editor={editor} />}
      <Separator orientation='vertical' className='mx-2 h-7' />

      <LinkEditPopover editor={editor} size='default' variant='default' />
    </div>
  </div>
);

export const TextEditorMinimal = React.forwardRef<HTMLDivElement, MinimalTiptapProps>(
  (
    {
      value,
      onChange,
      className,
      editorContentClassName,
      editable,
      maxLength,
      enableTextColor = false,
      ...props
    },
    ref
  ) => {
    const editor = useMinimalTiptapEditor({
      value,
      editable,
      onUpdate: () => {
        if (editor) {
          const contentText = editor.getText();

          if (maxLength && contentText.length > maxLength) {
            editor.commands.deleteRange({
              from: maxLength,
              to: contentText.length,
            });
            onChange?.(editor.getHTML());
          } else {
            onChange?.(editor.getHTML());
          }
        }
      },
      ...props,
    });

    if (!editor) {
      return null;
    }

    return (
      <MeasuredContainer
        as='div'
        name='editor'
        ref={ref}
        className={cn(
          editable
            ? 'flex h-auto min-h-72 w-full flex-col rounded-md border border-input shadow-sm'
            : '',
          className
        )}
      >
        {editable && <Toolbar editor={editor} enableTextColor={enableTextColor} />}
        <EditorContent
          editor={editor}
          className={cn('minimal-tiptap-editor', editorContentClassName)}
        />
        {editable && <LinkBubbleMenu editor={editor} />}
      </MeasuredContainer>
    );
  }
);

TextEditorMinimal.displayName = 'RichTextEditor';

export default TextEditorMinimal;
